import { FC } from 'react'

import {
  Box,
  Flex,
  GreyGrey70,
  IconCircleX,
  ListItem,
  Primary13Regular,
  Primary13SemiBold,
  Text,
  UnorderedList
} from '@sequencehq/core-components'

import { NodeTimeline } from 'Automations/components'
import { useManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTask'
import { IntegrationSyncErrorRecordCard } from './IntegrationSyncErrorRecordCard'
import {
  syncErrorDetail,
  syncErrorEventLabel,
  syncErrorExplanation,
  syncErrorFieldGroups
} from './integrationSyncErrorMessages'
import { FailingFieldGroup, SyncErrorTaskInput } from './types'

export const IntegrationSyncErrorPreview: FC<{ taskId: string }> = ({
  taskId
}) => {
  const { manualTask } = useManualTask({ taskId })
  const input = manualTask?.input as SyncErrorTaskInput | undefined

  if (!input) {
    return null
  }

  const detail = syncErrorDetail(input)

  return (
    <NodeTimeline.Item icon={<IconCircleX />} showStem={false} highlightIcon>
      <NodeTimeline.Title>
        <NodeTimeline.UserAction
          label={syncErrorEventLabel(input)}
          timestamp={manualTask?.createdAt}
        />
      </NodeTimeline.Title>

      <Flex flexDirection="column" gap="8px">
        <Text {...Primary13Regular} color={GreyGrey70}>
          {syncErrorExplanation(input)}
        </Text>

        {detail && (
          <Text
            {...Primary13Regular}
            color={GreyGrey70}
            data-testid="watchtower.notification.integrationSyncError.errorMessage"
          >
            {detail}
          </Text>
        )}

        {syncErrorFieldGroups(input).map(group => (
          <FailingFields key={group.problem} group={group} />
        ))}

        <IntegrationSyncErrorRecordCard taskId={taskId} />
      </Flex>
    </NodeTimeline.Item>
  )
}

const FailingFields: FC<{ group: FailingFieldGroup }> = ({ group }) => (
  <Box
    display="flex"
    flexDirection="column"
    gap="4px"
    data-testid={`watchtower.notification.integrationSyncError.fields.${group.problem}`}
  >
    <Text {...Primary13SemiBold} color={GreyGrey70}>
      {group.label}:
    </Text>
    <UnorderedList
      {...Primary13Regular}
      color={GreyGrey70}
      margin="0"
      paddingLeft="20px"
    >
      {group.fields.map(field => (
        <ListItem key={field}>{field}</ListItem>
      ))}
    </UnorderedList>
  </Box>
)
